import { useState, useCallback } from 'react';
import axios from 'axios';
import { useLogger } from 'components/common/LogViewer';

/**
 * Custom hook for handling file uploads, supporting both multipart and standard uploads
 * @param {Object} options - Configuration options
 * @param {Function} options.onUploadProgress - Callback for upload progress updates
 * @param {Function} options.onUploadComplete - Callback when upload completes successfully
 * @param {Function} options.onUploadError - Callback when upload fails
 * @param {Function} options.getMultipartUploadUrl - Function to get multipart upload URL for a part
 * @param {Function} options.completeMultipartUpload - Function to complete multipart upload
 * @param {Function} options.onRedirect - Optional callback for handling redirects on failure
 * @param {string} options.redirectId - Optional ID to use for redirects
 * @returns {Object} Upload state and functions
 */
const useFileUpload = ({
  onUploadProgress,
  onUploadComplete,
  onUploadError,
  getMultipartUploadUrl,
  completeMultipartUpload,
  onRedirect,
  redirectId
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(''); // 'validating', 'validated', 'uploading', 'processing', 'completed'
  const [currentFileName, setCurrentFileName] = useState('');
  const { logInfo, logWarning, logError, logApiError } = useLogger();

  /**
   * Upload a file using either multipart or standard upload
   * @param {string} url - Upload URL for standard uploads
   * @param {File} file - File to upload
   * @param {string} uploadId - Upload ID for multipart uploads
   * @param {string} mediaId - Media ID for multipart uploads
   * @returns {Promise<boolean>} Success status
   */
  const uploadFile = useCallback(
    async (url, file, uploadId = null, mediaId = null) => {
      try {
        logInfo('Starting file upload', {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          url,
          isMultipart: !!uploadId
        });

        // Set the current file name for display
        setCurrentFileName(file.name);

        // If we have uploadId and mediaId, this is a multipart upload
        if (uploadId && mediaId) {
          setUploadStage('uploading');

          // Dynamic chunk sizing based on file size
          // AWS S3 limits: 5MB minimum chunk, 10,000 parts maximum
          const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB minimum
          const MAX_PARTS = 10000; // AWS S3 limit

          // Calculate optimal chunk size based on file size
          let CHUNK_SIZE = Math.max(MIN_CHUNK_SIZE, Math.ceil(file.size / MAX_PARTS));

          // For larger files, use larger chunks to reduce the number of requests
          // if (file.size > 5 * 1024 * 1024 * 1024) {
          //   // > 5GB
          //   CHUNK_SIZE = Math.max(CHUNK_SIZE, 500 * 1024 * 1024); // Prefer 500MB chunks for very large files
          // } else if (file.size > 2 * 1024 * 1024 * 1024) {
          //   // > 2GB
          //   CHUNK_SIZE = Math.max(CHUNK_SIZE, 200 * 1024 * 1024); // Prefer 200MB chunks for large files
          // } else {
          //   CHUNK_SIZE = Math.max(CHUNK_SIZE, 100 * 1024 * 1024); // Default to 100MB chunks for 1-2GB files
          // }

          logInfo('Calculated optimal chunk size', {
            fileSize: file.size,
            chunkSize: CHUNK_SIZE,
            estimatedParts: Math.ceil(file.size / CHUNK_SIZE)
          });

          const chunks = Math.ceil(file.size / CHUNK_SIZE);
          const parts = [];

          // Upload each chunk sequentially
          for (let partNumber = 1; partNumber <= chunks; partNumber++) {
            let success = false;
            let attempts = 0;
            const MAX_ATTEMPTS = 5;

            // Keep trying until success or max attempts reached
            while (!success && attempts < MAX_ATTEMPTS) {
              attempts++;
              try {
                // Get upload URL for this part
                const { data: urlData } = await getMultipartUploadUrl(
                  uploadId,
                  mediaId,
                  partNumber
                );
                const { url: uploadUrl } = urlData;

                // Prepare the chunk
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(partNumber * CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);

                logInfo(
                  `Uploading part ${partNumber}/${chunks} (Attempt ${attempts}/${MAX_ATTEMPTS})`,
                  {
                    partNumber,
                    chunkSize: end - start,
                    start,
                    end
                  }
                );

                // Upload the chunk with timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000); // 5 minute timeout

                const response = await fetch(uploadUrl, {
                  method: 'PUT',
                  body: chunk,
                  signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                  throw new Error(
                    `Failed to upload part ${partNumber}: Server returned ${response.status}`
                  );
                }

                // Get ETag from response headers
                const etag = response.headers.get('ETag');
                if (!etag) {
                  throw new Error(`Server did not return ETag for part ${partNumber}`);
                }

                // Clean the ETag by removing surrounding quotes if present
                const cleanedEtag = etag.replace(/^"(.*)"$/, '$1');

                parts.push({
                  etag: cleanedEtag,
                  part_number: partNumber
                });

                // Mark as successful
                success = true;

                // Update progress
                const progress = Math.round((partNumber / chunks) * 100);
                setUploadProgress(progress);
                onUploadProgress?.(progress);

                // Log progress at meaningful intervals
                if (progress % 25 === 0) {
                  logInfo(`Multipart upload progress: ${progress}%`, {
                    part: partNumber,
                    totalParts: chunks,
                    fileName: file.name
                  });
                }
              } catch (error) {
                const isLastAttempt = attempts >= MAX_ATTEMPTS;
                const errorLevel = isLastAttempt ? 'error' : 'warning';

                logInfo(
                  `Part ${partNumber} upload ${
                    isLastAttempt ? 'failed' : 'retry'
                  } (Attempt ${attempts}/${MAX_ATTEMPTS})`,
                  {
                    error: error.message,
                    stack: error.stack,
                    level: errorLevel
                  }
                );

                if (isLastAttempt) {
                  logError(`Failed to upload part ${partNumber} after ${MAX_ATTEMPTS} attempts`);

                  // Handle redirect if configured
                  if (onRedirect && redirectId) {
                    logInfo(`Redirecting with ID: ${redirectId}`);
                    onRedirect(redirectId);
                    return false;
                  }

                  throw new Error(
                    `Failed to upload part ${partNumber} after ${MAX_ATTEMPTS} attempts: ${error.message}`
                  );
                }

                // Wait before retrying (exponential backoff)
                await new Promise(resolve => setTimeout(resolve, 1000 * attempts ** 2));
              }
            }
          }

          // Complete multipart upload
          try {
            // Validate parts before completion
            if (parts.length !== chunks) {
              throw new Error(`Missing parts: expected ${chunks}, got ${parts.length}`);
            }

            const partNumbers = parts.map(p => p.part_number);
            const expectedPartNumbers = Array.from({ length: chunks }, (_, i) => i + 1);
            const missingParts = expectedPartNumbers.filter(n => !partNumbers.includes(n));

            if (missingParts.length > 0) {
              throw new Error(`Missing part numbers: ${missingParts.join(', ')}`);
            }

            const invalidParts = parts.filter(p => !p.etag || p.etag.trim() === '');
            if (invalidParts.length > 0) {
              throw new Error(
                `Parts missing ETags: ${invalidParts.map(p => p.part_number).join(', ')}`
              );
            }

            logInfo('Attempting to complete multipart upload', {
              uploadId,
              mediaId,
              fileSize: file.size,
              partsCount: parts.length,
              chunkSize: CHUNK_SIZE
            });

            // Add a short delay before completion
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Complete the upload
            const completionResult = await completeMultipartUpload(uploadId, mediaId, parts);

            logInfo('Multipart file upload completed successfully', {
              fileName: file.name,
              result: completionResult?.data
            });

            setUploadStage('processing');
            onUploadComplete?.();
            return true;
          } catch (error) {
            logApiError('Error completing multipart upload', error);

            // Handle redirect if configured
            if (onRedirect && redirectId) {
              logInfo(`Multipart upload completion failed. Redirecting with ID: ${redirectId}`);
              onRedirect(redirectId);
              return false;
            }

            throw error;
          }
        } else {
          // Standard upload for smaller files
          const totalSize = file.size;
          setUploadStage('uploading');

          // Use the file provided, which should be the originalFile if available
          const fileToUpload = file.originalFile || file;

          await axios.put(url, fileToUpload, {
            headers: {
              'Content-Type': fileToUpload.type
            },
            onUploadProgress: progressEvent => {
              if (progressEvent.lengthComputable) {
                const uploadedSize = progressEvent.loaded;
                const progress = Math.round((uploadedSize / totalSize) * 100);
                setUploadProgress(progress);
                onUploadProgress?.(progress);

                // Log progress at meaningful intervals
                if (progress % 25 === 0) {
                  logInfo(`Upload progress: ${progress}%`, {
                    uploaded: uploadedSize,
                    total: totalSize,
                    fileName: fileToUpload.name
                  });
                }
              }
            }
          });

          logInfo('File upload completed successfully', { fileName: fileToUpload.name });
          setUploadStage('processing');
          onUploadComplete?.();
          return true;
        }
      } catch (error) {
        logApiError('Error uploading file', error);
        setUploadStage('');
        onUploadError?.(error);

        // Handle redirect if configured
        if (onRedirect && redirectId) {
          logInfo(`Upload failed. Redirecting with ID: ${redirectId}`);
          onRedirect(redirectId);
          return false;
        }

        throw error;
      }
    },
    [
      onUploadProgress,
      onUploadComplete,
      onUploadError,
      getMultipartUploadUrl,
      completeMultipartUpload,
      onRedirect,
      redirectId
    ]
  );

  return {
    uploadFile,
    uploadProgress,
    uploadStage,
    currentFileName,
    setUploadStage,
    setUploadProgress,
    setCurrentFileName
  };
};

export default useFileUpload;
